/**
 * FxCanvas — zero-dependency particle engine for client-side visual effects.
 *
 * One fixed, full-viewport <canvas> (pointer-events:none, above the DOM)
 * renders ALL particles in a single requestAnimationFrame loop. The
 * simulation (particle pool + step) is fully decoupled from rendering:
 *  - the canvas is created lazily (only in a real browser, on first render)
 *    so the engine is safe to construct in Node/jsdom environments,
 *  - rendering is a no-op when the 2D context is unavailable (jsdom),
 *  - clock and rAF are injectable for deterministic unit tests (the test
 *    drives step() manually instead of the auto rAF loop).
 *
 * Guardrails (keeps it light):
 *  - object-pooled particles with a hard live cap (default 400)
 *  - the rAF loop only runs while particles are alive
 *  - the simulation pauses while the tab is hidden (document.hidden)
 *  - prefers-reduced-motion: all spawns are no-ops
 *
 * Effects are fire-and-forget: spawning never blocks or throws into the
 * game state pipeline.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type FxKind =
  | 'sparks'      // short, fast, gravity-affected dots (hits, small impacts)
  | 'explosion'   // mixed dots + streaks + ring (minion deaths)
  | 'ring'        // expanding stroked circle (ability pulses)
  | 'puff'        // slow, soft, slightly rising dust (card slams)
  | 'confetti'    // swaying colored rectangles falling from the top (victory)
  | 'embers'      // slow rising orange dots (sudden death)
  | 'mist'        // soft drifting dots (necromancy spawns)
  | 'plus'        // green plus-sign particles bursting out (heals)
  | 'plusLine'    // a line of green plus-sign particles from source to target (Greater Heal)
  | 'boom'        // red/yellow fireball shooting outward across the battlefield (Explosive/Exploding)
  | 'shield'      // blue energy ring expanding outwards from the card center (Holy Shield)
  | 'wings'       // golden particle pair forming wings shooting out of the minion (Second Chance)
  | 'rays'        // white/yellow light rays shooting out of the minion (Transform)
  | 'blackSmoke'  // black smoke plume rising from a minion's slot (Rune of High Stakes destruction)
  | 'ice'         // large white ice/snow flakes bursting out and drifting down (Freeze)
  | 'hatch'       // egg-hatching: a white/cream flash ring + shell shards flying up and out (Swarmer spawns)
  | 'magic'       // violet/white sparkle burst + flash ring (spell cast)
  | 'star'        // white/gold 4-pointed STARS bursting out + flash ring (a spell's cast — the burst at the card's release point)
  | 'mystic'      // EXPLODING MYSTIC: a violet/purple/pink fireball burst + streaks + shockwave ring (a BATTLEFIELD-targeting spell's release point)
  | 'overdraw'    // RED CARD-LIKE EXPLOSION over the hand: red/white card shards + sparks + red shockwave ring (a draw that did not fit in a full hand — ANY overdraw, rune or not, task 186)
  | 'evaporate'   // a hand card evaporating into BURNING PARTICLES: rising orange/red embers that flicker and fade (the Sacrificial Rune's random hand-card destruction, task 186)
  | 'banX'        // BAN HAMMER (task 193): a LARGE GLOWING RED X pulsing over the target minion, then EXPLODING (red/orange sparks + red shockwave ring + white flash)
  | 'goldRain'    // ENRICHMENT (task 197): a GOLDEN RAIN of gold coins falling over the ENTIRE playfield (the cast pays the card's accrued gold)
  // AMBIENT (spell) — screen-wide effects, spawned continuously by
  // setAmbient() while active (the Acid Rain / Blizzard combat weather):
  | 'acidRain'    // green raindrops falling down the ENTIRE screen (Acid Rain spell)
  | 'blizzard';   // white snowflakes whirling across the ENTIRE screen (Blizzard spell)

export interface FxBurstOptions {
  /** Number of particles (kind defaults are used when omitted). */
  count?: number;
  /** Override the default color palette. */
  colors?: string[];
  /**
   * Uniform scale for the effect (default 1). Card-anchored effects pass
   * the card row's zoom (e.g. the board's --board-card-scale) so the
   * burst reads at the same size as the (zoomed) card it plays on —
   * without it the smoke/sparks look tiny next to large desktop cards.
   * Scales particle size, velocities, gravity and ring radii uniformly
   * (the whole effect magnifies like a zoom, keeping its shape).
   */
  scale?: number;
}

export interface FxCanvasOptions {
  /** Hard cap on live particles. Default 400. */
  maxParticles?: number;
  /** Force reduced-motion mode (spawns no-op). Default: read from matchMedia. */
  reducedMotion?: boolean;
  /** Injectable clock (ms). Default Date.now. */
  now?: () => number;
  /** Injectable rAF. Default requestAnimationFrame. Tests pass a no-op. */
  raf?: (cb: (t: number) => void) => number;
  /** Injectable cancelAnimationFrame. Default cancelAnimationFrame. */
  cancelRaf?: (id: number) => void;
}

interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Seconds of life remaining. */
  life: number;
  /** Total lifetime in seconds (for alpha fade). */
  maxLife: number;
  size: number;
  color: string;
  gravity: number;
  /** Velocity decay per second (0..1 fraction kept per frame-second). */
  drag: number;
  /** Render shape. */
  shape: 'dot' | 'streak' | 'ring' | 'rect' | 'plus' | 'star' | 'x';
  /**
   * Seconds before the particle activates (it is held at its spawn point,
   * invisible, while the delay runs). Used for staged effects (the Ban
   * Hammer's explosion fires only after the pulsing X has played).
   */
  delay: number;
  /** For ring shape: final radius when life runs out. */
  ringRadius: number;
  /** For rect shape: rotation (rad) and angular velocity (rad/s). */
  rotation: number;
  vr: number;
}

// ─── Palettes ───────────────────────────────────────────────────────────────

const DEFAULT_COLORS: Record<FxKind, string[]> = {
  sparks: ['#ffd166', '#ff9f43', '#f39c12', '#fff3bf'],
  explosion: ['#ff6b35', '#f7c531', '#ff9f43', '#ffffff', '#e84118'],
  ring: ['#ffffff', '#dfe6e9'],
  puff: ['#dfe6e9', '#b2bec3', '#ffffff'],
  confetti: ['#f7c531', '#ff6b6b', '#4ecdc4', '#a29bfe', '#55efc4', '#fd79a8'],
  embers: ['#ff6b35', '#f7c531', '#e84118', '#ffa502'],
  mist: ['#55efc4', '#00b894', '#81ecec', '#2ecc71'],
  plus: ['#55efc4', '#2ecc71', '#a8ff78', '#00b894'],
  plusLine: ['#55efc4', '#2ecc71', '#a8ff78', '#00b894'], // same heal green as 'plus'
  // Boom: the Explosive/Exploding fireball — red and yellow only.
  boom: ['#ff4757', '#ff6b35', '#f7c531', '#ffd166', '#fff3bf'],
  // Holy Shield: a holy blue — the energy wave that rolls out of the card.
  shield: ['#74b9ff', '#0984e3', '#a29bfe', '#dfe6e9'],
  // Second Chance: pure gold — the wings that shoot out of the minion.
  wings: ['#f7c531', '#ffeaa7', '#fdcb6e', '#ffd166'],
  // Transform: white light with warm gold — the rays that shoot out of the
  // minion the moment it becomes another class.
  rays: ['#ffffff', '#fff9e6', '#f7c531', '#ffeaa7', '#fff3bf'],
  // High Stakes destruction: BLACK smoke — the minion was consumed. No
  // fire, no ring: just a dark plume that swallows the slot it occupied.
  blackSmoke: ['#000000', '#111111', '#1a1a1a', '#2d3436', '#3b3b3b'],
  // Freeze: pure white with a cold ice-blue tint — the large snow/ice
  // flakes that burst out of the frozen minion.
  ice: ['#ffffff', '#f0f8ff', '#dfe6e9', '#a2d2ff', '#74b9ff'],
  // Swarmer hatch: warm eggshell whites and creams — the flash of the
  // cracking shell and the shards it throws out.
  hatch: ['#ffffff', '#fff9e6', '#ffeaa7', '#fdf6e3'],
  // Spell cast: violet magic with white sparkles — the burst that plays
  // when a spell's effect goes into play (targeted: on the buffed minion,
  // battlefield: at the board center).
  magic: ['#d2b4ff', '#a29bfe', '#6c5ce7', '#ffffff', '#845ef7'],
  // Spell cast star: white light with warm gold — the stars that burst out
  // of the point where the spell card was released.
  star: ['#ffffff', '#fff9e6', '#f7c531', '#ffeaa7', '#fff3bf'],
  // Exploding mystic: the ARCANE fireball — violets and purples with a
  // magenta edge and white-hot sparkles (a battlefield spell detonating
  // mystic energy at its release point).
  mystic: ['#a29bfe', '#6c5ce7', '#d2b4ff', '#fd79a8', '#ffffff', '#845ef7'],
  // Acid Rain: the spell's poison-green raindrops (the Toxic Cloud's
  // slime-green palette — this is ACID, not holy water).
  acidRain: ['#9acd32', '#2ecc71', '#a8ff78', '#55efc4'],
  // Blizzard: the spell's snowflakes — pure whites with a cold blue tint
  // (the Freeze's ice palette).
  blizzard: ['#ffffff', '#f0f8ff', '#dfe6e9', '#a2d2ff'],
  // Overdraw (task 186): the RED card-like explosion — reds with hot-white
  // card-edge shards (a card detonating in the hand).
  overdraw: ['#ff4757', '#ff6b6b', '#e84118', '#ffffff', '#fff3bf'],
  // Enrichment (task 197): the golden rain — golds (coins falling over the
  // playfield).
  goldRain: ['#f1c40f', '#ffd700', '#f39c12', '#ffe082', '#fff3bf'],
  // Evaporate (task 186): burning particles — the deep-fire ember palette
  // (a card dissolving into burning motes).
  evaporate: ['#ff6b35', '#f7c531', '#e84118', '#ffa502', '#ffd166'],
  // Ban Hammer (task 193): the glowing red X + its explosion — deep reds
  // with a hot edge and a white-hot flash core.
  banX: ['#ff1a1a', '#ff4757', '#ff6b35', '#ffd166', '#ffffff'],
};

// Shared Pain: the red line of pain that expands from the holder to every
// marked victim (painLineEl) — pure reds, no yellow (this is HURT, not
// explosion).
const PAIN_LINE_COLORS = ['#ff4757', '#ff6b6b', '#e84118', '#ff073a'];
// Toxic Cloud: the green cloud that rolls over the poisoned battlefield
// (cloudEl) — the Blightcaster's slime-green palette.
const TOXIC_CLOUD_COLORS = ['#9acd32', '#2ecc71', '#a8ff78', '#55efc4'];

const GOLD_COLORS = ['#f7c531', '#ffeaa7', '#fdcb6e'];
const RED_COLORS = ['#ff6b6b', '#ff4757', '#ffa8a8'];

// ─── Ability → FX mapping ───────────────────────────────────────────────────

export interface AbilityFxSpec {
  kind: FxKind;
  colors?: string[];
  /** Particle count (for kinds that take a count; ignored by 'ring'). */
  count?: number;
}

/**
 * Signature look per ability (keyed by ability ID, the same IDs the server
 * sends in CombatAbilityEvent.abilityId). Unknown abilities fall back to a
 * neutral white ring via fxForAbility().
 */
export const ABILITY_FX: Record<string, AbilityFxSpec> = {
  lifesteal: { kind: 'sparks', colors: ['#ff4757', '#ff6b81', '#c0392b'] },
  shield: { kind: 'ring', colors: ['#74b9ff', '#a29bfe', '#dfe6e9'] },
  // Explosive / Exploding: the red/yellow BOOM burst (wireCombatAnimationView
  // additionally emits the "BOOM!" text at the source for both).
  explosive: { kind: 'boom' },
  exploding: { kind: 'boom' },
  // Killer: a RED burst — the full-heal revival is dramatic and must be
  // obviously visible at a glance ("it healed itself!").
  killer: { kind: 'sparks', colors: RED_COLORS, count: 26 },
  draw: { kind: 'mist', colors: ['#74b9ff', '#a29bfe', '#dfe6e9'] },
  fortress: { kind: 'ring', colors: ['#b2bec3', '#dfe6e9', '#ffffff'] },
  blast: { kind: 'explosion', colors: ['#ff9f43', '#f7c531', '#ffffff'] },
  warband: { kind: 'sparks', colors: GOLD_COLORS },
  necromancy: { kind: 'mist', colors: ['#55efc4', '#00b894', '#81ecec'] },
  gravedigger: { kind: 'mist', colors: ['#b2bec3', '#8395a7', '#dfe6e9'] },
  improved: { kind: 'sparks', colors: ['#55efc4', '#81ecec'] },
  // Greater Heal's signature look is the plusLine sweep (wired in
  // wireCombatAnimationView for the 'ability' event) — this entry is the
  // fallback burst at the caster when the sweep cannot resolve targets.
  greater_heal: { kind: 'plus' },
  // ATK buff at the caster (the buffed neighbor shows the gold aura
  // sparks via the aura event that follows in the stream).
  buff_attack: { kind: 'sparks', colors: GOLD_COLORS },
  // Holy Shield: the blue shield wave expanding outwards from the card
  // center (the 'ability' event fires at the shielded unit when an Undead
  // hit is halved).
  holy_shield: { kind: 'shield' },
  // Second Chance: the golden wing pair shooting out of the minion (the
  // 'ability' event fires at the saved unit when a lethal hit is negated).
  second_chance: { kind: 'wings' },
  // Blightcaster kit: Mark — a dark ring at the caster (the continuous
  // black-particle emitter at the TARGET is wired from the dedicated
  // CombatMarkEvent, not from this ability event); Blunt — a dull gray
  // spark at the caster (the ATK numbers themselves update via the aura
  // event that follows).
  mark: { kind: 'ring', colors: ['#2d3436', '#636e72', '#000000'] },
  blunt: { kind: 'sparks', colors: ['#636e72', '#b2bec3', '#2d3436'], count: 8 },
  // Glasscannon (Swarmer): the glass shatters as the swarmer dies after its
  // attack — a crisp pale-blue/white spark burst at the swarmer.
  glasscannon: { kind: 'sparks', colors: ['#dfe6e9', '#a2d2ff', '#ffffff', '#74b9ff'], count: 18 },
  // SwarmLord: the swarm-stir burst as the holder dies (the spawns
  // themselves play the per-card 'hatch' FX from CombatSwarmEvent).
  swarmlord: { kind: 'ring', colors: ['#fff9e6', '#ffeaa7', '#ffffff'] },
  // Divine Blessing (spell-granted keyword): the LARGE YELLOW EXPLOSION on
  // the minion when its first-damage charge fires (the 'ability' event
  // streams from takeDamage's trigger queue — the explosion kind = mixed
  // dots + streaks + shockwave ring, in a pure gold/yellow palette).
  divine_blessing: { kind: 'explosion', colors: ['#f7c531', '#ffd166', '#ffeaa7', '#f39c12', '#ffffff'], count: 34 },
  // LifeBurst (Healer, task 206): green plus-sign burst at the dying
  // holder (the board-wide flash + all-board bursts ride the dedicated
  // lifeBurst event).
  life_burst: { kind: 'plus', colors: ['#55efc4', '#2ecc71', '#a8ff78', '#00b894'], count: 20 },
};

/** Returns the FX spec for an ability ID (neutral ring fallback). */
export function fxForAbility(abilityId: string): AbilityFxSpec {
  return ABILITY_FX[abilityId] ?? { kind: 'ring' };
}

// ─── Engine ─────────────────────────────────────────────────────────────────

/** Small deterministic-ish RNG (mulberry32) — keeps particle motion lively
 *  without pulling in a math lib; seed is arbitrary per engine instance. */
function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class FxCanvas {
  private readonly _maxParticles: number;
  private readonly _reducedMotion: boolean;
  private readonly _now: () => number;
  private readonly _raf: (cb: (t: number) => void) => number;
  private readonly _cancelRaf: (id: number) => void;
  private readonly _rng: () => number;

  /** Transient uniform scale for the in-flight spawn call (1 = normal).
   *  Set by burstAt/trailEl/… from the caller's FxBurstOptions.scale / scale
   *  argument and applied in _spawnParticle — ONE place covers every kind.
   *  Always 1 outside a spawn call (restored in the finally blocks). */
  private _fxScale = 1;

  /** Particle pool (fixed size = cap). */
  private readonly _pool: Particle[] = [];
  private _freeList: number[] = [];
  private _liveCount: number = 0;

  private _canvas: HTMLCanvasElement | null = null;
  private _rafId: number | null = null;
  private _lastTick: number | null = null;
  private _disposed: boolean = false;
  private _onVisibility: (() => void) | null = null;
  private _onResize: (() => void) | null = null;
  /**
   * AMBIENT effects (the spell combat weather — Acid Rain's green rain,
   * the Blizzard's whirling snowflakes): the kinds to keep spawning
   * continuously across the whole screen (setAmbient). step() spawns a
   * few particles per frame per kind (the viewport is the "screen" — the
   * effects are not tied to any board element). Empty/null = no ambient
   * weather. Spawning respects reduced motion (spawns are no-ops) and the
   * live-particle cap (a full pool simply drops the overflow).
   */
  private _ambientKinds: FxKind[] = [];
  /** Ambient spawn rate per kind (particles per second). */
  private static readonly AMBIENT_RATES: Partial<Record<FxKind, number>> = {
    acidRain: 110,
    blizzard: 70,
  };

  constructor(options: FxCanvasOptions = {}) {
    this._maxParticles = options.maxParticles ?? 400;
    this._now = options.now ?? (() => Date.now());
    this._raf = options.raf ?? ((cb) => {
      if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(cb);
      return 0;
    });
    this._cancelRaf = options.cancelRaf ?? ((id) => {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
    });
    this._rng = makeRng(0x9e3779b9);
    this._reducedMotion =
      options.reducedMotion ?? FxCanvas.detectReducedMotion();

    for (let i = 0; i < this._maxParticles; i++) {
      this._pool.push({
        active: false, x: 0, y: 0, vx: 0, vy: 0,
        life: 0, maxLife: 1, size: 2, color: '#fff',
        gravity: 0, drag: 1, shape: 'dot', ringRadius: 0,
        rotation: 0, vr: 0, delay: 0,
      });
      this._freeList.push(i);
    }

    // Pause/resume with tab visibility (browser only).
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      this._onVisibility = () => {
        if (document.hidden) {
          this._stopLoop();
        } else {
          this._lastTick = null; // avoid a huge dt jump
          this._kickLoop();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this._onResize = () => this._resizeCanvas();
      window.addEventListener('resize', this._onResize);
    }
  }

  /** Reads prefers-reduced-motion (guarded for non-browser environments). */
  static detectReducedMotion(): boolean {
    try {
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    } catch {
      /* non-browser or unsupported — motion is fine */
    }
    return false;
  }

  /**
   * Sets the AMBIENT screen-wide weather effects (the spell combat FX:
   * 'acidRain' — green raindrops falling down the entire screen; 'blizzard'
   * — white snowflakes whirling around the entire screen). Pass an array of
   * kinds to run (both can run at once), or null/[] to stop. The effects
   * spawn continuously in step() until cleared (the caller stops them when
   * the combat ends — see main.ts' combat-area show/hide). No-op under
   * reduced motion (spawns are suppressed), and spawning is capped by the
   * pool like every other effect. Safe in non-browser environments (the
   * simulation runs, nothing is drawn).
   */
  setAmbient(kinds: FxKind[] | null): void {
    if (this._disposed) return;
    this._ambientKinds = kinds ? [...kinds] : [];
  }

  /** The kinds currently spawning ambient weather (tests/diagnostics). */
  get ambientKinds(): FxKind[] {
    return [...this._ambientKinds];
  }

  /** Number of live particles (tests/diagnostics). */
  get liveParticleCount(): number {
    return this._liveCount;
  }

  /** True when spawns are suppressed (reduced motion). */
  get isReducedMotion(): boolean {
    return this._reducedMotion;
  }

  // ─── Spawn API ────────────────────────────────────────────────────────────

  /** Spawns a particle burst at viewport coordinates. */
  burstAt(x: number, y: number, kind: FxKind, opts: FxBurstOptions = {}): void {
    if (this._reducedMotion || this._disposed) return;
    // The transient scale is applied in _spawnParticle (one place covers
    // every kind); restored in the finally so nested/sequential spawns
    // from other code paths always see 1.
    const prevScale = this._fxScale;
    this._fxScale = opts.scale ?? 1;
    try {
    switch (kind) {
      case 'sparks': this._spawnSparks(x, y, opts.count ?? 10, opts.colors ?? DEFAULT_COLORS.sparks); break;
      case 'explosion': this._spawnExplosion(x, y, opts.count ?? 26, opts.colors ?? DEFAULT_COLORS.explosion); break;
      case 'ring': this._spawnRing(x, y, opts.colors?.[0] ?? '#ffffff'); break;
      case 'puff': this._spawnPuff(x, y, opts.count ?? 8, opts.colors ?? DEFAULT_COLORS.puff); break;
      case 'embers': this._spawnEmbers(x, y, opts.count ?? 24, opts.colors ?? DEFAULT_COLORS.embers); break;
      case 'mist': this._spawnMist(x, y, opts.count ?? 16, opts.colors ?? DEFAULT_COLORS.mist); break;
      case 'plus': this._spawnPlus(x, y, opts.count ?? 12, opts.colors ?? DEFAULT_COLORS.plus); break;
      case 'boom': this._spawnBoom(x, y, opts.count ?? 44, opts.colors ?? DEFAULT_COLORS.boom); break;
      case 'shield': this._spawnShield(x, y, opts.count ?? 12, opts.colors ?? DEFAULT_COLORS.shield); break;
      case 'wings': this._spawnWings(x, y, opts.count ?? 28, opts.colors ?? DEFAULT_COLORS.wings); break;
      case 'rays': this._spawnRays(x, y, opts.count ?? 36, opts.colors ?? DEFAULT_COLORS.rays); break;
      case 'blackSmoke': this._spawnBlackSmoke(x, y, opts.count ?? 26, opts.colors ?? DEFAULT_COLORS.blackSmoke); break;
      case 'ice': this._spawnIce(x, y, opts.count ?? 30, opts.colors ?? DEFAULT_COLORS.ice); break;
      case 'hatch': this._spawnHatch(x, y, opts.count ?? 24, opts.colors ?? DEFAULT_COLORS.hatch); break;
      case 'magic': this._spawnMagic(x, y, opts.count ?? 36, opts.colors ?? DEFAULT_COLORS.magic); break;
      case 'star': this._spawnStar(x, y, opts.count ?? 28, opts.colors ?? DEFAULT_COLORS.star); break;
      case 'mystic': this._spawnMystic(x, y, opts.count ?? 38, opts.colors ?? DEFAULT_COLORS.mystic); break;
      case 'overdraw': this._spawnOverdraw(x, y, opts.count ?? 34, opts.colors ?? DEFAULT_COLORS.overdraw); break;
      case 'evaporate': this._spawnEvaporate(x, y, opts.count ?? 30, opts.colors ?? DEFAULT_COLORS.evaporate); break;
      case 'banX': this._spawnBanX(x, y); break;
      case 'goldRain': this._spawnGoldRain(opts.count ?? 70, opts.colors ?? DEFAULT_COLORS.goldRain);
      case 'confetti': this._spawnConfetti(opts.count ?? 80, opts.colors ?? DEFAULT_COLORS.confetti);
    }
    this._kickLoop();
    } finally {
      this._fxScale = prevScale;
    }
  }

  /** Spawns a burst at the center of a DOM element (viewport coordinates).
   *  No-op when the element is missing or has no measurable position
   *  (detached / hidden — a 0×0 rect would spawn at the viewport corner). */
  burstEl(el: Element | null, kind: FxKind, opts: FxBurstOptions = {}): void {
    const r = this._rect(el);
    if (!r) return;
    this.burstAt(r.left + r.width / 2, r.top + r.height / 2, kind, opts);
  }

  /** Expanding ring at the center of a DOM element (ability pulses).
   *  No-op when the element is missing or has no measurable position.
   *  `scale` magnifies the ring to the card's zoom (see FxBurstOptions). */
  ringEl(el: Element | null, color?: string, scale?: number): void {
    const r = this._rect(el);
    if (!r) return;
    this.burstAt(r.left + r.width / 2, r.top + r.height / 2, 'ring', {
      colors: color ? [color] : undefined,
      scale,
    });
  }

  /**
   * A line of green plus-sign particles traveling from the center of one
   * element toward the center of another (Greater Heal's signature look:
   * the heal wave shoots out of the caster and sweeps OVER a friendly
   * minion). The particles follow the line like the attack spark trail,
   * but as green plus signs (the heal shape). No-op when either element
   * is missing or has no measurable position (or reduced motion).
   */
  plusLineEl(from: Element | null, to: Element | null, colors: string[] = DEFAULT_COLORS.plus, scale?: number): void {
    const a = this._rect(from);
    const b = this._rect(to);
    if (!a || !b) return;
    if (this._reducedMotion || this._disposed) return;
    const prevScale = this._fxScale;
    this._fxScale = scale ?? 1;
    try {
    const x1 = a.left + a.width / 2;
    const y1 = a.top + a.height / 2;
    const x2 = b.left + b.width / 2;
    const y2 = b.top + b.height / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.max(Math.hypot(dx, dy), 1);
    const ux = dx / dist;
    const uy = dy / dist;
    const count = Math.min(16, 7 + Math.floor(dist / 35));
    for (let i = 0; i < count; i++) {
      const t = this._rng();
      // spread around the line, velocity along it (caster → target)
      const px = x1 + dx * t + (this._rng() - 0.5) * 16;
      const py = y1 + dy * t + (this._rng() - 0.5) * 16;
      const speed = 260 + this._rng() * 140;
      this._spawnParticle({
        x: px, y: py,
        vx: ux * speed + (this._rng() - 0.5) * 40,
        vy: uy * speed + (this._rng() - 0.5) * 40,
        life: 0.3 + this._rng() * 0.25,
        size: 3 + this._rng() * 3,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.92,
        shape: 'plus',
        rotation: this._rng() * Math.PI * 2,
        vr: (this._rng() - 0.5) * 4,
      });
    }
    this._kickLoop();
    } finally {
      this._fxScale = prevScale;
    }
  }

  /** Spark trail along the line between two DOM elements (attacks).
   *  No-op when either element is missing or has no measurable position. */
  trailEl(from: Element | null, to: Element | null, colors: string[] = DEFAULT_COLORS.sparks, scale?: number): void {
    const a = this._rect(from);
    const b = this._rect(to);
    if (!a || !b) return;
    if (this._reducedMotion || this._disposed) return;
    const prevScale = this._fxScale;
    this._fxScale = scale ?? 1;
    try {
    const x1 = a.left + a.width / 2;
    const y1 = a.top + a.height / 2;
    const x2 = b.left + b.width / 2;
    const y2 = b.top + b.height / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.max(Math.hypot(dx, dy), 1);
    const ux = dx / dist;
    const uy = dy / dist;
    const count = Math.min(14, 6 + Math.floor(dist / 40));
    for (let i = 0; i < count; i++) {
      const t = this._rng();
      // spread around the line, velocity along the line
      const px = x1 + dx * t + (this._rng() - 0.5) * 14;
      const py = y1 + dy * t + (this._rng() - 0.5) * 14;
      const speed = 220 + this._rng() * 160;
      this._spawnParticle({
        x: px, y: py,
        vx: ux * speed + (this._rng() - 0.5) * 60,
        vy: uy * speed + (this._rng() - 0.5) * 60,
        life: 0.18 + this._rng() * 0.2,
        size: 1.5 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.9,
        shape: 'streak',
      });
    }
    this._kickLoop();
    } finally {
      this._fxScale = prevScale;
    }
  }

  /**
   * The red expanding particle line of Shared Pain: red streaks travel from
   * the center of the holder toward the center of a marked victim (the pain
   * is drawn FROM the holder TO the victim that also receives the damage)
   * and an expanding red ring marks the impact at the victim. No-op when
   * either element is missing or has no measurable position (or reduced
   * motion).
   */
  painLineEl(from: Element | null, to: Element | null, colors: string[] = PAIN_LINE_COLORS, scale?: number): void {
    const a = this._rect(from);
    const b = this._rect(to);
    if (!a || !b) return;
    if (this._reducedMotion || this._disposed) return;
    const prevScale = this._fxScale;
    this._fxScale = scale ?? 1;
    try {
    const x1 = a.left + a.width / 2;
    const y1 = a.top + a.height / 2;
    const x2 = b.left + b.width / 2;
    const y2 = b.top + b.height / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.max(Math.hypot(dx, dy), 1);
    const ux = dx / dist;
    const uy = dy / dist;
    // The line: streaks spread around the path, velocity along it
    // (holder → victim), so the line visibly EXPANDS from the holder.
    const count = Math.min(18, 8 + Math.floor(dist / 30));
    for (let i = 0; i < count; i++) {
      const t = this._rng();
      const px = x1 + dx * t + (this._rng() - 0.5) * 12;
      const py = y1 + dy * t + (this._rng() - 0.5) * 12;
      const speed = 320 + this._rng() * 200;
      this._spawnParticle({
        x: px, y: py,
        vx: ux * speed + (this._rng() - 0.5) * 50,
        vy: uy * speed + (this._rng() - 0.5) * 50,
        life: 0.22 + this._rng() * 0.22,
        size: 1.5 + this._rng() * 2.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.9,
        shape: 'streak',
      });
    }
    // The impact: one expanding red ring at the victim.
    this._spawnParticle({
      x: x2, y: y2, vx: 0, vy: 0,
      life: 0.4, size: 3,
      color: colors[0] ?? '#ff4757',
      shape: 'ring', ringRadius: 34,
    });
    this._kickLoop();
    } finally {
      this._fxScale = prevScale;
    }
  }

  /**
   * The Toxic Cloud: a green cloud rolling over an ENTIRE element (the
   * poisoned player's battlefield — the caller passes the side container).
   * Soft mist-like puffs are spread across the container's width and drift
   * slowly upward while lingering, so the cloud reads as a bank of poison
   * fog rather than a burst. No-op when the element is missing or has no
   * measurable position (or reduced motion).
   */
  cloudEl(el: Element | null, colors: string[] = TOXIC_CLOUD_COLORS, scale?: number): void {
    const r = this._rect(el);
    if (!r) return;
    if (this._reducedMotion || this._disposed) return;
    const prevScale = this._fxScale;
    this._fxScale = scale ?? 1;
    try {
    const count = 26;
    for (let i = 0; i < count; i++) {
      // Spread across the whole container (the cloud covers the side),
      // vertically centered on the minion row.
      const px = r.left + this._rng() * r.width;
      const py = r.top + r.height * (0.3 + this._rng() * 0.4);
      const speed = 20 + this._rng() * 40;
      const angle = this._rng() * Math.PI * 2;
      this._spawnParticle({
        x: px, y: py,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 25,
        life: 0.8 + this._rng() * 0.7,
        size: 6 + this._rng() * 9,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -25,
        drag: 0.92,
      });
    }
    this._kickLoop();
    } finally {
      this._fxScale = prevScale;
    }
  }

  /** Confetti falling from the top of the viewport (victory celebration). */
  confetti(count = 80): void {
    if (this._reducedMotion || this._disposed) return;
    this._spawnConfetti(count, DEFAULT_COLORS.confetti);
    this._kickLoop();
  }

  /**
   * Advances the simulation by the given timestamp (ms) and renders.
   * Driven automatically by the rAF loop in browsers; tests call it with
   * their own clock. dt is clamped so a hidden tab or a slow frame never
   * explodes particle motion.
   */
  step(now: number = this._now()): void {
    if (this._disposed) return;
    if (this._lastTick === null) {
      this._lastTick = now;
      return;
    }
    const dt = Math.min((now - this._lastTick) / 1000, 0.05);
    this._lastTick = now;
    if (dt > 0) this._advance(dt);
    // Ambient spell weather (Acid Rain / Blizzard): a few particles per
    // frame per active kind, spread across the WHOLE viewport (the effects
    // are screen-wide — "falling onto the battlefield" / "wirveling around
    // the screen"). No-op under reduced motion (the spawn guards) and under
    // the live-particle cap (a full pool drops the overflow).
    if (dt > 0) this._spawnAmbient(dt);
    this._render();
    if (this._liveCount === 0 && this._ambientKinds.length === 0) {
      this._stopLoop();
    }
  }

  /**
   * Spawns the active ambient weather particles for this frame (see
   * setAmbient). dt-scaled: each kind spawns rate × dt particles (the rate
   * in AMBIENT_RATES). Raindrops spawn just above the top edge and fall
   * fast; snowflakes spawn anywhere on the screen and whirl (drift + sway
   * + slow fall). Non-ambient kinds are ignored.
   */
  private _spawnAmbient(dt: number): void {
    if (this._reducedMotion || this._ambientKinds.length === 0) return;
    const width = typeof window !== 'undefined' ? window.innerWidth : 800;
    const height = typeof window !== 'undefined' ? window.innerHeight : 600;
    for (const kind of this._ambientKinds) {
      const rate = FxCanvas.AMBIENT_RATES[kind];
      if (!rate) continue;
      const count = Math.max(1, Math.round(rate * dt));
      if (kind === 'acidRain') {
        for (let i = 0; i < count; i++) {
          this._spawnParticle({
            x: this._rng() * width,
            y: -8 - this._rng() * 24,
            vx: (this._rng() - 0.5) * 30,
            vy: 420 + this._rng() * 260, // rain falls FAST
            life: 0.9 + this._rng() * 0.5,
            size: 1.5 + this._rng() * 2.5,
            color: DEFAULT_COLORS.acidRain[Math.floor(this._rng() * DEFAULT_COLORS.acidRain.length)],
            gravity: 320,
            drag: 0.995,
            shape: 'streak', // a streak trailing behind the velocity = a raindrop
          });
        }
      } else if (kind === 'blizzard') {
        for (let i = 0; i < count; i++) {
          this._spawnParticle({
            x: this._rng() * width,
            y: this._rng() * height, // snow is ALREADY on the screen (it whirs in place)
            vx: 40 + this._rng() * 120, // drift (a blizzard blows sideways)
            vy: 15 + this._rng() * 45, // ...while falling slowly
            life: 1.4 + this._rng() * 1.2,
            size: 2 + this._rng() * 4.5,
            color: DEFAULT_COLORS.blizzard[Math.floor(this._rng() * DEFAULT_COLORS.blizzard.length)],
            gravity: 18,
            drag: 0.995,
          });
        }
      }
    }
  }

  /** Removes all live particles immediately. */
  clear(): void {
    for (const p of this._pool) {
      p.active = false;
    }
    // Rebuild the free list from scratch. The old code appended every index
    // without clearing the list first, which left duplicate entries behind
    // (a popped duplicate could then re-allocate a slot that is still live,
    // double-counting _liveCount and overwriting an in-flight particle).
    this._freeList.length = 0;
    for (let i = 0; i < this._maxParticles; i++) this._freeList.push(i);
    this._liveCount = 0;
    // A full reset also stops the ambient spell weather (Acid Rain /
    // Blizzard) — otherwise the next step() would resume spawning.
    this._ambientKinds.length = 0;
    this._stopLoop();
    this._render(); // clear the canvas right away
  }

  /** Releases the canvas and event listeners. Idempotent. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._stopLoop();
    if (this._canvas && this._canvas.parentElement) {
      this._canvas.parentElement.removeChild(this._canvas);
    }
    this._canvas = null;
    if (this._onVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
    if (this._onResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this._onResize);
      this._onResize = null;
    }
  }

  // ─── Simulation ──────────────────────────────────────────────────────────

  private _advance(dt: number): void {
    for (let i = 0; i < this._pool.length; i++) {
      const p = this._pool[i];
      if (!p.active) continue;
      if (p.delay > 0) {
        // Held at the spawn point, invisible — the delay runs off real
        // time but life (and the staging) starts only when it elapses.
        p.delay -= dt;
        continue;
      }
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        this._liveCount--;
        // Return the slot to the free list. Without this the pool only ever
        // SHRINKS: after the first `maxParticles` cumulative spawns the free
        // list is empty and every later spawn is a silent no-op, so the
        // particle effects stop working permanently (the reported "FX dies
        // after a while" bug).
        this._freeList.push(i);
        continue;
      }
      p.vy += p.gravity * dt;
      const keep = Math.pow(p.drag, dt * 60);
      p.vx *= keep;
      p.vy *= keep;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.vr * dt;
    }
  }

  /** Allocates one particle (no-op when the cap is reached). */
  private _spawnParticle(spec: {
    x: number; y: number; vx: number; vy: number; life: number;
    size: number; color: string; gravity?: number; drag?: number;
    shape?: Particle['shape']; ringRadius?: number; rotation?: number; vr?: number;
    delay?: number;
  }): boolean {
    const idx = this._freeList.pop();
    if (idx === undefined) return false; // cap reached
    const p = this._pool[idx];
    const s = this._fxScale; // caller's uniform scale (1 when unset)
    p.active = true;
    p.x = spec.x;
    p.y = spec.y;
    // Spawn delay (staged effects — the Ban Hammer's explosion waits for
    // the pulsing X). NOT scaled: the staging rhythm is the effect's
    // design, not its size.
    p.delay = spec.delay ?? 0;
    // Velocity/gravity/radius scale with size so the whole effect
    // magnifies uniformly (same shape, same flight time relative to its
    // own size) when it plays on a zoomed card.
    p.vx = spec.vx * s;
    p.vy = spec.vy * s;
    p.life = spec.life;
    p.maxLife = spec.life;
    p.size = spec.size * s;
    p.color = spec.color;
    p.gravity = (spec.gravity ?? 0) * s;
    p.drag = spec.drag ?? 1;
    p.shape = spec.shape ?? 'dot';
    p.ringRadius = (spec.ringRadius ?? 0) * s;
    p.rotation = spec.rotation ?? 0;
    p.vr = spec.vr ?? 0;
    this._liveCount++;
    return true;
  }

  private _spawnSparks(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 60 + this._rng() * 200;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: 0.25 + this._rng() * 0.3,
        size: 1.5 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 320,
        drag: 0.92,
      });
    }
  }

  private _spawnExplosion(x: number, y: number, count: number, colors: string[]): void {
    this._spawnSparks(x, y, count, colors);
    // a few faster streaks
    for (let i = 0; i < 6; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 260 + this._rng() * 180;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.2 + this._rng() * 0.2,
        size: 2 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 240,
        drag: 0.9,
        shape: 'streak',
      });
    }
    // shockwave ring
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.35, size: 2,
      color: colors[0] ?? '#ffffff',
      shape: 'ring', ringRadius: 46,
    });
  }

  private _spawnRing(x: number, y: number, color: string): void {
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.5, size: 3, color,
      shape: 'ring', ringRadius: 40,
    });
  }

  /**
   * RED CARD-LIKE EXPLOSION (task 186 — an OVERDRAW: a drawn card did not
   * fit in the full hand and is lost). The look of a card detonating:
   * a red shockwave ring, hot-white/red card-edge SHARDS (rotating rects
   * — the card's pieces) and a burst of red sparks. Plays over the hand
   * strip of the player whose draw was lost (any overdraw — the
   * Sacrificial Rune only adds the +5/+5 board buff and the hand-card
   * evaporation on top, it never suppresses this).
   */
  /**
   * RED CARD-LIKE EXPLOSION (task 186 — an OVERDRAW: a drawn card did not
   * fit in the full hand and is lost; task 196 — BIG and SLOW: the effect
   * must be noticed, so the blast is much larger and the whole sequence
   * unfolds over ~2 SECONDS, not a blink). The look of a card detonating:
   * a white-hot inner flash, TWO big red shockwave rings rolling out
   * across the hand area, hot-white/red card-edge SHARDS (big rotating
   * rects — the pieces of the card, drifting out slowly) and a long burst
   * of red sparks. Plays over the hand strip of the player whose draw was
   * lost (any overdraw — the Sacrificial Rune only adds the +5/+5 board
   * buff and the hand-card evaporation on top, it never suppresses this).
   */
  private _spawnOverdraw(x: number, y: number, count: number, colors: string[]): void {
    // The white-hot flash at the detonation core (brief and bright).
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.5, size: 5,
      color: '#fff3bf',
      shape: 'ring', ringRadius: 70,
    });
    // Two BIG red shockwave rings — the blast rolling out over the whole
    // hand strip (rings grow 6px → ringRadius over their life; the outer
    // one is slower and larger so the blast lingers ~2 s).
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 1.4, size: 4,
      color: colors[0] ?? '#ff4757',
      shape: 'ring', ringRadius: 240,
    });
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 2.0, size: 3,
      color: colors[1] ?? '#ff6b6b',
      shape: 'ring', ringRadius: 340,
    });
    // Card-edge shards: BIG rotating white/red rectangles scattering out
    // SLOWLY — the pieces of the lost card, drifting across the hand area
    // (long lives, low gravity: they float and tumble, not fall).
    const shards = Math.max(12, Math.floor(count / 2));
    for (let i = 0; i < shards; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 70 + this._rng() * 260;
      const white = this._rng() < 0.45;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed * 0.7 - 30,
        life: 1.0 + this._rng() * 1.0,
        size: 6 + this._rng() * 10,
        color: white ? '#ffffff' : colors[Math.floor(this._rng() * 3)],
        gravity: 140,
        drag: 0.9,
        shape: 'rect',
        rotation: this._rng() * Math.PI * 2,
        vr: (this._rng() - 0.5) * 8,
      });
    }
    // Red sparks filling the gaps (long-lived embers).
    for (let i = 0; i < count - shards; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 50 + this._rng() * 220;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed * 0.7 - 20,
        life: 0.9 + this._rng() * 0.9,
        size: 2.5 + this._rng() * 3.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 200,
        drag: 0.92,
      });
    }
  }

  /**
   * BURNING PARTICLES (task 186 — the SACRIFICIAL RUNE's random hand-card
   * destruction): a card evaporating into burning motes — embers rising
   * from the card's last position, flickering as they drift up and fade
   * (a little sideways wander + buoyancy, no fall — they dissolve upward
   * into the air, leaving a brief ember trail).
   */
  private _spawnEvaporate(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      // Spawn across a card-sized patch (a hand card's footprint) so the
      // whole card dissolves, not a single point.
      const px = x + (this._rng() - 0.5) * 46;
      const py = y + (this._rng() - 0.5) * 60;
      this._spawnParticle({
        x: px, y: py,
        vx: (this._rng() - 0.5) * 40,
        vy: -(30 + this._rng() * 90), // rise
        life: 0.5 + this._rng() * 0.6,
        size: 1.5 + this._rng() * 3,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -30, // buoyancy — keep climbing as they fade
        drag: 0.96,
      });
    }
    // A few larger, slower ember cores for depth.
    for (let i = 0; i < 6; i++) {
      const px = x + (this._rng() - 0.5) * 30;
      const py = y + (this._rng() - 0.5) * 40;
      this._spawnParticle({
        x: px, y: py,
        vx: (this._rng() - 0.5) * 20,
        vy: -(20 + this._rng() * 50),
        life: 0.7 + this._rng() * 0.5,
        size: 2.5 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -20,
        drag: 0.97,
        shape: 'streak',
      });
    }
  }

  /**
   * BAN HAMMER (task 193): the large glowing red X over the target minion,
   * pulsing for BAN_X_PULSE_SECONDS, then EXPLODING — red/orange sparks
   * radially out, a red shockwave ring, and a brief white-hot flash.
   * Staged via the particle `delay` (the explosion parts are held at the
   * spawn point while the X plays; they all fire the instant the X dies).
   */
  private _spawnBanX(x: number, y: number): void {
    const colors = DEFAULT_COLORS.banX;
    const pulseSeconds = 0.9; // the X's life — the explosion's delay
    // The mark itself: one big pulsing glowing X, centered, motionless.
    this._spawnParticle({
      x, y,
      vx: 0, vy: 0,
      life: pulseSeconds,
      size: 55, // half bar length (the pulse swells it to ≈74 at the peak)
      color: colors[0], // '#ff1a1a'
      shape: 'x',
    });
    // The explosion — everything delayed by the X's full life so the mark
    // completes its pulses before detonating.
    const explosionDelay = pulseSeconds;
    for (let i = 0; i < 30; i++) {
      const angle = (i / 30) * Math.PI * 2 + (this._rng() - 0.5) * 0.4;
      const speed = 120 + this._rng() * 260;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.45 + this._rng() * 0.4,
        size: 2.5 + this._rng() * 3,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.94,
        shape: 'streak',
        delay: explosionDelay,
      });
    }
    // The red shockwave ring (expands out of the minion's center).
    this._spawnParticle({
      x, y,
      vx: 0, vy: 0,
      life: 0.5,
      size: 4,
      color: colors[0],
      shape: 'ring',
      ringRadius: 110,
      delay: explosionDelay,
    });
    // The white-hot flash core (a fast, short, big dot).
    this._spawnParticle({
      x, y,
      vx: 0, vy: 0,
      life: 0.18,
      size: 46,
      color: '#ffffff',
      delay: explosionDelay,
    });
  }

  private _spawnPuff(x: number, y: number, count: number, colors: string[]): void {
    // Big, dramatic dust burst — this is the card-slam effect, so it should
    // clearly fill the area around the card that just landed on the board.
    // LARGER (playtest, task 177: "the smoke effect when we play a card
    // should be larger") — wider spawn area, faster spread, bigger dust.
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 60 + this._rng() * 150;
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * 110,
        y: y + (this._rng() - 0.5) * 70,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 30,
        life: 0.6 + this._rng() * 0.6,
        size: 10 + this._rng() * 12,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -30, // dust floats up
        drag: 0.9,
      });
    }
  }

  private _spawnEmbers(x: number, y: number, count: number, colors: string[]): void {
    const width = typeof window !== 'undefined' ? window.innerWidth : 800;
    for (let i = 0; i < count; i++) {
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * width * 0.8,
        y: y + (this._rng() - 0.5) * 60,
        vx: (this._rng() - 0.5) * 40,
        vy: -30 - this._rng() * 60,
        life: 0.8 + this._rng() * 0.9,
        size: 1.5 + this._rng() * 2.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -18, // embers rise
        drag: 0.98,
      });
    }
  }

  private _spawnMist(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 15 + this._rng() * 50;
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * 30,
        y: y + (this._rng() - 0.5) * 30,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 20,
        life: 0.6 + this._rng() * 0.7,
        size: 3 + this._rng() * 5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -20,
        drag: 0.9,
      });
    }
  }

  /**
   * Green plus-sign particles bursting out of the healed card (the Heal
   * keyword's signature look). Particles scatter radially from the card
   * center, drift gently upward, and tumble slowly while fading.
   */
  private _spawnPlus(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 50 + this._rng() * 150;
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * 18,
        y: y + (this._rng() - 0.5) * 18,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 45, // upward bias — healing drifts up
        life: 0.5 + this._rng() * 0.4,
        size: 4 + this._rng() * 4,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -25, // plus signs float gently
        drag: 0.9,
        shape: 'plus',
        rotation: this._rng() * Math.PI * 2,
        vr: (this._rng() - 0.5) * 6, // slow tumble
      });
    }
  }

  /**
   * The Explosive/Exploding signature look — a SMOKE AND FIRE detonation
   * shooting out of the dead minion across the ENTIRE battlefield:
   *  - a wide, fast radial FIREBALL burst (the red/orange/yellow fire —
   *    higher speeds and longer lives than 'explosion' so the particles
   *    reach far corners of the board),
   *  - a few high-speed fire streaks carrying the burst even further,
   *  - a plume of SMOKE: big, slow, dark puffs that billow outward and RISE
   *    (negative gravity) well after the fire fades — the long-lived part
   *    of the effect (1.1–2.0 s, vs the fire's 0.55–1.05 s),
   *  - a bright inner flash ring (the moment of detonation) plus the large
   *    shockwave ring across the board.
   */
  private _spawnBoom(x: number, y: number, count: number, colors: string[]): void {
    // ── Fire: the wide, fast fireball burst ─────────────────────────────
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 240 + this._rng() * 560;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.55 + this._rng() * 0.5,
        // Big fireball dots (≈2× the old 2–6 px) — the burst must read as
        // a genuine explosion across the board, not a sprinkle of specks.
        size: 4 + this._rng() * 6,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 240,
        drag: 0.95,
      });
    }
    // A few faster, thicker fire streaks carry the burst even further.
    for (let i = 0; i < 8; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 440 + this._rng() * 300;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.25 + this._rng() * 0.2,
        size: 3 + this._rng() * 3,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 200,
        drag: 0.92,
        shape: 'streak',
      });
    }
    // ── Smoke: the slow dark plume that lingers and RISES after the fire ─
    // Darker toward the end of life is faked with the palette (no
    // per-life tinting in the pool) — charcoal grays, one near-black core.
    const smokeColors = ['#4a4a4a', '#5a5a5a', '#6e6e6e', '#3b3b3b'];
    for (let i = 0; i < 18; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 25 + this._rng() * 65; // billow, don't shoot
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * 14,
        y: y + (this._rng() - 0.5) * 14,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed * 0.6 - (28 + this._rng() * 34), // rise bias
        life: 1.1 + this._rng() * 0.9,
        // Big soft puffs — the smoke cloud must read as volume, not specks.
        size: 9 + this._rng() * 10,
        color: smokeColors[Math.floor(this._rng() * smokeColors.length)],
        gravity: -22, // smoke rises
        drag: 0.93,
      });
    }
    // ── Shockwave: bright inner flash + the large ring across the board ─
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.25, size: 4,
      color: '#fff3bf', // the white-hot flash of the detonation
      shape: 'ring', ringRadius: 90,
    });
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.5, size: 3,
      color: colors[0] ?? '#ffffff',
      shape: 'ring', ringRadius: 210,
    });
  }

  /**
   * The Holy Shield signature look: a blue energy wave rolling OUTWARDS
   * from the CENTER of the shielded card — two expanding stroked rings
   * (a bright inner wave and a wider, softer outer one) plus a small
   * shimmer of slow blue dots drifting out with the wave.
   */
  private _spawnShield(x: number, y: number, count: number, colors: string[]): void {
    // The main expanding wave (rings grow from 6px to ringRadius over life).
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.55, size: 4,
      color: colors[0] ?? '#74b9ff',
      shape: 'ring', ringRadius: 70,
    });
    // A wider, softer trailing wave.
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.7, size: 2.5,
      color: colors[2] ?? colors[0] ?? '#a29bfe',
      shape: 'ring', ringRadius: 105,
    });
    // Blue shimmer dots drifting out with the wave.
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 40 + this._rng() * 110;
      this._spawnParticle({
        x: x + Math.cos(angle) * 6,
        y: y + Math.sin(angle) * 6,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.35 + this._rng() * 0.3,
        size: 1.5 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.9,
      });
    }
  }

  /**
   * The Second Chance signature look: golden particles forming a PAIR OF
   * WINGS shooting out of the saved minion — two mirrored arcs (one per
   * wing) of gold dots that start close to the card center and sweep
   * outwards and up like spreading wings, with a small gold flash at the
   * center where the charge fires.
   */
  private _spawnWings(x: number, y: number, count: number, colors: string[]): void {
    const perWing = Math.max(Math.floor(count / 2), 4);
    // Arc angles (canvas coords, y down): from up-and-in (-66°) sweeping
    // down-and-out (+37°) — the wing shape.
    const sweepStart = -1.15;
    const sweepEnd = 0.65;
    for (const side of [-1, 1]) {
      for (let i = 0; i < perWing; i++) {
        const t = i / Math.max(perWing - 1, 1); // 0 = wing base, 1 = wing tip
        const base = sweepStart + t * (sweepEnd - sweepStart);
        const angle = side === 1 ? base : Math.PI - base; // mirror the left wing
        const r = 12 + t * 26; // the wing grows outward along the sweep
        this._spawnParticle({
          x: x + Math.cos(angle) * r,
          y: y + Math.sin(angle) * r,
          vx: Math.cos(angle) * (170 + t * 190 + this._rng() * 60),
          vy: Math.sin(angle) * (170 + t * 190 + this._rng() * 60) - 30,
          life: 0.45 + this._rng() * 0.35,
          size: 3 + t * 3 + this._rng() * 2, // broader toward the wing tip
          color: colors[Math.floor(this._rng() * colors.length)],
          gravity: 60, // the wings droop gently as they spread
          drag: 0.9,
        });
      }
    }
    // Gold flash at the center where the charge fires.
    for (let i = 0; i < 8; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 80 + this._rng() * 140;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: 0.25 + this._rng() * 0.2,
        size: 2 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 120,
        drag: 0.92,
      });
    }
  }

  /**
   * The Rune of High Stakes DESTRUCTION signature look: a plume of BLACK
   * SMOKE billowing up out of the destroyed minion's slot — big, slow,
   * near-black puffs that spread slightly and RISE (negative gravity) well
   * after the card is gone. Deliberately lifeless compared to the boom's
   * smoke (no fire, no flash ring, no shockwave): the card was consumed,
   * not exploded, so only the dark smoke lingers over the empty slot.
   */
  private _spawnBlackSmoke(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 20 + this._rng() * 55; // billow, don't shoot
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * 34,
        y: y + (this._rng() - 0.5) * 26,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed * 0.5 - (26 + this._rng() * 34), // rise bias
        life: 0.9 + this._rng() * 0.9,
        // Big soft puffs — the smoke must read as volume, not specks.
        size: 8 + this._rng() * 10,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: -20, // smoke rises
        drag: 0.93,
      });
    }
  }

  /**
   * The Transform signature look: a burst of WHITE/YELLOW LIGHT RAYS
   * shooting out of the transformed minion — long, fast streaks radiating
   * in ALL directions (the rays, evenly fanned with a little jitter so the
   * fan reads as a burst), a few bright core dots at the heart of the
   * flash, and a white flash ring at the moment of the transformation.
   */
  private _spawnRays(x: number, y: number, count: number, colors: string[]): void {
    // The rays: fast streaks radiating outwards in all directions. The
    // streak shape trails behind the velocity vector, so each particle
    // reads as a line of light shooting away from the card.
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + (this._rng() - 0.5) * 0.35;
      const speed = 320 + this._rng() * 320;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.3 + this._rng() * 0.25,
        size: 2 + this._rng() * 2.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.9,
        shape: 'streak',
      });
    }
    // A few brighter, slower core dots at the heart of the burst.
    for (let i = 0; i < 10; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 60 + this._rng() * 160;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.25 + this._rng() * 0.2,
        size: 1.5 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.92,
      });
    }
    // The white flash ring at the moment of the transformation.
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.35, size: 3,
      color: '#ffffff',
      shape: 'ring', ringRadius: 60,
    });
  }

  /**
   * The Freeze signature look: LARGE white ice/snow flakes bursting out of
   * the frozen minion and drifting DOWN as they fall (the card is encased in
   * frost, not hit by fire). The flakes are big (≈2× the boom's fire dots),
   * slow to leave the card (a radial burst that stays over the minion), then
   * sway gently while falling under a mild gravity for a long life (up to
   * ~1.5 s), so the snow reads as a lingering frost cloud over the card.
   */
  private _spawnIce(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 30 + this._rng() * 130; // a wide, gentle burst
      this._spawnParticle({
        x: x + Math.cos(angle) * 8,
        y: y + Math.sin(angle) * 8,
        vx: Math.cos(angle) * speed + (this._rng() - 0.5) * 50, // + sway
        vy: Math.sin(angle) * speed - 20, // slight upward kick before the fall
        life: 0.9 + this._rng() * 0.6,
        // Big flakes — the effect must read as snow, not a sprinkle of specks.
        size: 4 + this._rng() * 8,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 90, // the flakes fall
        drag: 0.94,
      });
    }
    // A cold flash ring at the moment of the freeze (the frost sets).
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.4, size: 3,
      color: '#dfe6e9',
      shape: 'ring', ringRadius: 52,
    });
  }

  /**
   * Spell cast: a fast violet/white sparkle burst (radial, slight upward
   * kick) + an expanding flash ring — the magic that goes into play when a
   * spell is cast. Short-lived and punchy (≈0.7 s).
   */
  private _spawnMagic(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 120 + this._rng() * 260; // fast, energetic burst
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 60, // upward kick — sparks rise then fall
        life: 0.45 + this._rng() * 0.35,
        size: 2 + this._rng() * 3.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 260,
        drag: 0.9,
      });
    }
    // A violet flash ring at the moment of the cast.
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.45, size: 3,
      color: '#a29bfe',
      shape: 'ring', ringRadius: 70,
    });
  }

  /**
   * STAR explosion (a spell's cast — the burst at the point where the spell
   * card was RELEASED: the drag drop point, or the cast card's position for
   * button/double-click casts). White/gold 4-pointed stars shoot out in all
   * directions, spinning, under a light gravity (they rise a touch then
   * fall — the sparkle of magic being spent), plus a warm flash ring at the
   * moment of the cast. Brighter and spikier than 'magic' (which stays
   * reserved for the other magic sites) — this is the signature "the spell
   * was released HERE" moment.
   */
  private _spawnStar(x: number, y: number, count: number, colors: string[]): void {
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 100 + this._rng() * 300;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: 0.5 + this._rng() * 0.4,
        size: 3 + this._rng() * 4,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 220,
        drag: 0.88,
        shape: 'star',
        rotation: this._rng() * Math.PI,
        vr: (this._rng() - 0.5) * 12,
      });
    }
    // A warm flash ring at the moment of the release.
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.4, size: 3,
      color: '#f7c531',
      shape: 'ring', ringRadius: 60,
    });
  }

  /**
   * The EXPLODING MYSTIC (a battlefield-targeting spell's release point —
   * Healing Rain / Acid Rain / Blizzard & co. detonate here, NOT on a
   * minion): an arcane fireball — a wide, fast violet/purple/pink radial
   * burst with a few high-speed mystic streaks, a white-hot flash ring at
   * the moment of detonation and a large violet shockwave ring rolling
   * outwards (bigger than a normal explosion's — this is a board-wide
   * cast). The playfield flash (flashBattlefield) plays on top from
   * main.ts.
   */
  private _spawnMystic(x: number, y: number, count: number, colors: string[]): void {
    // The arcane fireball: a fast, wide radial burst of mystic energy.
    for (let i = 0; i < count; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 160 + this._rng() * 460;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.45 + this._rng() * 0.45,
        size: 3 + this._rng() * 5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 160,
        drag: 0.9,
      });
    }
    // A few high-speed mystic streaks carry the burst further.
    for (let i = 0; i < 8; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 380 + this._rng() * 260;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.28 + this._rng() * 0.2,
        size: 2 + this._rng() * 2.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        drag: 0.88,
        shape: 'streak',
      });
    }
    // White-hot flash at the moment of detonation + the violet shockwave.
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.3, size: 4,
      color: '#ffffff',
      shape: 'ring', ringRadius: 70,
    });
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.55, size: 3,
      color: '#a29bfe',
      shape: 'ring', ringRadius: 150,
    });
  }

  /**
   * Egg-hatching burst (Swarmer spawns — every spawned swarmer card plays
   * this exactly once, from the CombatSwarmEvent handler in main.ts). The
   * look: a bright white/cream flash ring (the crack) + shell shards
   * (rotating rects) flying UP and out under gravity, each swaying a little
   * as it falls — the swarmer bursts out of its egg. Warm, not hostile:
   * the palette is eggshell white/cream, never red.
   */
  private _spawnHatch(x: number, y: number, count: number, colors: string[]): void {
    // The crack: a bright flash ring at the moment the shell breaks.
    this._spawnParticle({
      x, y, vx: 0, vy: 0,
      life: 0.35, size: 3,
      color: '#ffffff',
      shape: 'ring', ringRadius: 44,
    });
    // Shell shards: small rotating rects launched up and out, falling back
    // under a strong gravity (the shell pieces tumble down around the card).
    for (let i = 0; i < count; i++) {
      // Upward-biased cone: -150°..-30° (over the top of the card) with a
      // few pieces flying straight out to the sides.
      const angle = -Math.PI / 2 + (this._rng() - 0.5) * Math.PI * 1.1;
      const speed = 70 + this._rng() * 190;
      this._spawnParticle({
        x: x + (this._rng() - 0.5) * 10,
        y: y + (this._rng() - 0.5) * 10,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.45 + this._rng() * 0.35,
        size: 2.5 + this._rng() * 3.5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 520,
        drag: 0.96,
        shape: 'rect',
        rotation: this._rng() * Math.PI * 2,
        vr: (this._rng() - 0.5) * 14, // the shards tumble
      });
    }
    // The glint of the swarmer inside: a few small white dots at the center
    // (the hatch flash itself).
    for (let i = 0; i < 6; i++) {
      const angle = this._rng() * Math.PI * 2;
      const speed = 20 + this._rng() * 60;
      this._spawnParticle({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.25 + this._rng() * 0.2,
        size: 1.5 + this._rng() * 2,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 0,
        drag: 0.9,
      });
    }
  }

  private _spawnConfetti(count: number, colors: string[]): void {
    const width = typeof window !== 'undefined' ? window.innerWidth : 800;
    for (let i = 0; i < count; i++) {
      this._spawnParticle({
        x: this._rng() * width,
        y: -10 - this._rng() * 40,
        vx: (this._rng() - 0.5) * 120,
        vy: 60 + this._rng() * 140,
        life: 1.8 + this._rng() * 1.4,
        size: 4 + this._rng() * 5,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 220,
        drag: 0.995,
        shape: 'rect',
        rotation: this._rng() * Math.PI * 2,
        vr: (this._rng() - 0.5) * 10,
      });
    }
  }

  /**
   * GOLDEN RAIN (task 197 — the Enrichment cast): GOLD COINS falling over
   * the ENTIRE screen (the whole playfield — the gold the card paid). The
   * coins are small glowing gold dots/streaks with a gentle sway, falling
   * over ~1.5–2.5 s (a shower, not a downpour — the cast's reward moment).
   * Screen-wide like the confetti/ambient rains (ignores the burst
   * position).
   */
  private _spawnGoldRain(count: number, colors: string[]): void {
    const width = typeof window !== 'undefined' ? window.innerWidth : 800;
    for (let i = 0; i < count; i++) {
      this._spawnParticle({
        x: this._rng() * width,
        y: -10 - this._rng() * 60, // staggered above the top edge
        vx: (this._rng() - 0.5) * 60,
        vy: 140 + this._rng() * 220,
        life: 1.4 + this._rng() * 1.1,
        size: 3 + this._rng() * 4,
        color: colors[Math.floor(this._rng() * colors.length)],
        gravity: 260,
        drag: 0.995,
        shape: 'dot',
      });
    }
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  private _ensureCanvas(): HTMLCanvasElement | null {
    if (this._canvas) return this._canvas;
    if (typeof document === 'undefined' || !document.body) return null;
    const canvas = document.createElement('canvas');
    canvas.id = 'fx-canvas';
    canvas.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:99990;';
    document.body.appendChild(canvas);
    this._canvas = canvas;
    this._resizeCanvas();
    return canvas;
  }

  private _resizeCanvas(): void {
    if (!this._canvas || typeof window === 'undefined') return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this._canvas.width = Math.max(1, Math.floor(w * dpr));
    this._canvas.height = Math.max(1, Math.floor(h * dpr));
    this._canvas.style.width = `${w}px`;
    this._canvas.style.height = `${h}px`;
    const ctx = this._canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private _render(): void {
    // Skip rendering entirely when nothing is alive (keeps idle cost zero).
    if (this._liveCount === 0) {
      if (this._canvas) {
        const ctx = this._canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
      }
      return;
    }
    const canvas = this._ensureCanvas();
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom — simulation still works, nothing to draw
    if (this._lastTick === null || this._canvas === canvas) {
      // no-op; transform is set in _resizeCanvas
    }
    ctx.clearRect(0, 0, window.innerWidth || canvas.width, window.innerHeight || canvas.height);
    for (const p of this._pool) {
      if (!p.active || p.delay > 0) continue; // delayed particles are held invisibly
      const alpha = Math.max(p.life / p.maxLife, 0);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      ctx.strokeStyle = p.color;
      switch (p.shape) {
        case 'dot':
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'streak': {
          ctx.lineWidth = p.size;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 0.04, p.y - p.vy * 0.04);
          ctx.stroke();
          break;
        }
        case 'ring': {
          const progress = 1 - p.life / p.maxLife;
          const radius = 6 + p.ringRadius * progress;
          ctx.lineWidth = Math.max(p.size * (1 - progress * 0.7), 0.5);
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'rect':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          ctx.restore();
          break;
        case 'plus': {
          // A plus sign: two crossing bars (size = half the bar length).
          const arm = p.size;
          const thick = Math.max(p.size * 0.4, 1.5);
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillRect(-thick / 2, -arm, thick, arm * 2); // vertical bar
          ctx.fillRect(-arm, -thick / 2, arm * 2, thick); // horizontal bar
          ctx.restore();
          break;
        }
        case 'star': {
          // A 4-pointed star (size = the outer radius): two crossing
          // diamond spikes — vertical (long) + horizontal (slightly
          // shorter), spinning with the particle's rotation.
          const r = p.size;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.beginPath();
          ctx.moveTo(0, -r);
          ctx.quadraticCurveTo(0, 0, r * 0.62, 0);
          ctx.quadraticCurveTo(0, 0, 0, r);
          ctx.quadraticCurveTo(0, 0, -r * 0.62, 0);
          ctx.quadraticCurveTo(0, 0, 0, -r);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
          break;
        }
        case 'x': {
          // A GLOWING X (the Ban Hammer mark, task 193): two crossing bars
          // at 45° (a plus sign rotated π/4). The whole X PULSES — its size
          // swells with a sine over its life (two full pulses) — and a red
          // shadowBlur glow makes it read as light, not paint. The linear
          // alpha fade over the life keeps the ending soft before the
          // explosion takes over.
          const progress = 1 - p.life / p.maxLife;
          const pulse = 0.8 + 0.35 * Math.sin(progress * Math.PI * 4);
          const arm = p.size * pulse; // size = half the bar length
          const thick = Math.max(arm * 0.22, 2);
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.PI / 4);
          ctx.shadowColor = p.color;
          ctx.shadowBlur = 18 * pulse;
          ctx.fillRect(-thick / 2, -arm, thick, arm * 2); // one bar
          ctx.fillRect(-arm, -thick / 2, arm * 2, thick); // the other
          ctx.restore();
          break;
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  // ─── Loop management ─────────────────────────────────────────────────────

  private _kickLoop(): void {
    if (this._disposed || this._rafId !== null || this._reducedMotion) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const tick = () => {
      this._rafId = null;
      if (this._disposed) return;
      if (typeof document !== 'undefined' && document.hidden) return; // resume via visibilitychange
      this.step(this._now());
      if (this._liveCount > 0) {
        this._rafId = this._raf(tick);
      }
    };
    this._rafId = this._raf(tick);
  }

  private _stopLoop(): void {
    if (this._rafId !== null) {
      this._cancelRaf(this._rafId);
      this._rafId = null;
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private _rect(el: Element | null | undefined): { left: number; top: number; width: number; height: number } | null {
    if (!el) return null;
    try {
      const r = el.getBoundingClientRect();
      // A zero-size rect means the element is detached from the DOM or
      // inside a hidden (display:none) subtree — it has no meaningful
      // position. Spawning from it would fling the burst to the viewport's
      // top-left corner, completely missing the card (the "FX plays at the
      // wrong position" bug). Treat it as "no position" and skip, the same
      // convention FxTriggers.floatText uses.
      if (r.width === 0 && r.height === 0) return null;
      return r;
    } catch {
      return null;
    }
  }
}

/** Red color palette exported for HUD/ability float effects. */
export { RED_COLORS as FX_RED_COLORS, GOLD_COLORS as FX_GOLD_COLORS };
